import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';
import { CheckOutlined, CheckCircleFilled } from '@ant-design/icons';

import { useModal, useCopyToClipboard } from '@/hooks';

import { COMMON_CONTAINER_ID } from '@/config';
import { useAppSelector } from '@/redux/hooks';
import { profileSelectors, userSelectors } from '@/redux/selectors';

import {
  Button,
  QrCodeModal,
  SectionBox,
  SocialsContactModal,
  SvgIcon,
} from '@/components';

import YourReferralLinksModal from '../YourReferralLinksModal/YourReferralLinksModal';
import { YourReferralLinksCreateModal } from '../YourReferralLinksCreateModal';
import { YourReferralLinksEditModal } from '../YourReferralLinksEditModal';

import styles from './ReferralProgram.module.sass';

interface ReferralProgramProps {}

const ReferralProgram: FC<ReferralProgramProps> = () => {
  const { t } = useTranslation();
  const [, copy] = useCopyToClipboard(false);

  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  const [refLinkOnEdit, setRefLinkOnEdit] = useState(null);
  const [qrLink, setQrLink] = useState<string | null>(null);

  const {
    isOpen: isOpenYourReferralLinksModal,
    onOpenModal: handleOpenYourReferralLinksModal,
    onCloseModal: handleCloseYourReferralLinksModal,
  } = useModal();

  const {
    isOpen: isOpenYourReferralLinksEditModal,
    onOpenModal: handleOpenYourReferralLinksEditModal,
    onCloseModal: handleCloseYourReferralLinksEditModal,
  } = useModal();

  const {
    isOpen: isOpenYourReferralLinksAddModal,
    onOpenModal: handleOpenYourReferralLinksAddModal,
    onCloseModal: handleCloseYourReferralLinksAddModal,
  } = useModal();

  const {
    isOpen: isOpenSocialsContactModal,
    onOpenModal: handleOpenSocialsContactModal,
    onCloseModal: handleCloseSocialsContactModal,
  } = useModal();

  const {
    isOpen: isOpenQrModal,
    onOpenModal: handleOpenQrModal,
    onCloseModal: handleCloseQrModal,
  } = useModal();

  const openEditRefCode = (reflink: any) => {
    setRefLinkOnEdit(reflink);
    handleOpenYourReferralLinksEditModal();
  };

  const openQrCode = (link: string) => {
    setQrLink(link);
    handleOpenQrModal();
  };

  const handleCopyLink = async (link: string) => {
    const success = await copy(link);

    if (success) {
      toast.success(`${link} ${t('translation.is_copied_to_clipboard')}`, {
        containerId: COMMON_CONTAINER_ID,
        icon: <CheckCircleFilled className={styles.copyToastIcon} />,
        className: styles.copyToast,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const hasRefProgram = !!userData?.refProgram;
  const refData = profileData?.refData;
  const primaryLink = profileData?.refLinks?.[0] ?? '';

  const stats = [
    {
      icon: 'percent',
      label: t('translation.your_kickback_fee'),
      value: `${refData?.feePercent ?? 0}%`,
    },
    {
      icon: 'users',
      label: t('translation.referals_number_label'),
      value: `${refData?.count ?? 0}`,
    },
    {
      icon: 'trend-up',
      label: t('translation.commission_earned'),
      value: `${refData?.bal ?? 0}`,
      unit: 'USDT',
    },
    {
      icon: 'dollar-sign',
      label: t('translation.commission_paid'),
      value: `${refData?.wds ?? 0}`,
      unit: 'USDT',
    },
  ];

  return (
    <SectionBox>
      {{
        body: (
          <div className={styles.referral}>
            <div className={styles.header}>
              <h3 className={styles.title}>
                {t('translation.referral_program_title')}
              </h3>
              <div className={styles.badge}>
                <CheckOutlined className={styles.icon} />
                {t('translation.referral_earn_badge')}
              </div>
            </div>

            <div className={styles.description}>
              {parse(t('translation.user_reflink_advert_str'))}
            </div>

            {hasRefProgram ? (
              <>
                <div className={styles.invite}>
                  <div className={styles.info}>
                    <div className={styles.title}>
                      {t('translation.referral_invite_title')}
                    </div>
                    <div className={styles.subtitle}>
                      {t('translation.referral_invite_subtitle')}
                    </div>
                  </div>

                  {primaryLink && (
                    <div className={styles.actions}>
                      <div className={styles.link}>{primaryLink}</div>

                      <Button
                        type="primary"
                        corner="rect"
                        className={styles.copy}
                        onClick={() => handleCopyLink(primaryLink)}
                      >
                        <SvgIcon name="copy" className={styles.icon} />
                        {t('translation.referral_copy_link')}
                      </Button>

                      <button
                        type="button"
                        className={styles.qr}
                        onClick={() => openQrCode(primaryLink)}
                        aria-label={t('translation.referral_qr_title')}
                      >
                        <SvgIcon name="qr-code" className={styles.icon} />
                      </button>
                    </div>
                  )}
                </div>

                <h4 className={styles.statsTitle}>
                  {t('translation.referral_program_statisctics')}
                </h4>

                <div className={styles.stats}>
                  {stats.map((stat) => (
                    <div className={styles.stat} key={stat.label}>
                      <div className={styles.iconWrap}>
                        <SvgIcon name={stat.icon} className={styles.icon} />
                      </div>
                      <div className={styles.label}>{stat.label}</div>
                      <div className={styles.value}>
                        {stat.value}
                        {stat.unit && (
                          <span className={styles.unit}> {stat.unit}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Button
                className={styles.cta}
                type="secondary"
                corner="rect"
                onClick={handleOpenSocialsContactModal}
              >
                {t('translation.get_ref_link')}
              </Button>
            )}

            <YourReferralLinksModal
              isOpen={isOpenYourReferralLinksModal}
              onClose={handleCloseYourReferralLinksModal}
              onEdit={openEditRefCode}
              onAddNew={handleOpenYourReferralLinksAddModal}
            />

            <YourReferralLinksEditModal
              reflink={refLinkOnEdit}
              isOpen={isOpenYourReferralLinksEditModal}
              onClose={handleCloseYourReferralLinksEditModal}
            />

            <YourReferralLinksCreateModal
              isOpen={isOpenYourReferralLinksAddModal}
              onClose={handleCloseYourReferralLinksAddModal}
            />

            <SocialsContactModal
              title={t('translation.referral_link_msg')}
              onClose={handleCloseSocialsContactModal}
              isOpen={isOpenSocialsContactModal}
            />

            <QrCodeModal
              isActive={isOpenQrModal}
              title={t('translation.referral_qr_title')}
              url={qrLink || ''}
              onClose={handleCloseQrModal}
            />
          </div>
        ),
      }}
    </SectionBox>
  );
};

export default ReferralProgram;
